/* import __COLOCATED_TEMPLATE__ from './sq-option-set.hbs'; */
import Component from '@ember/component';
import { A } from '@ember/array';

/**
`SqOptionComponent` and `SqOptionSetComponent` offer a flexible solution for
`<input type='radio'>` and/or `<select> <option> </select>` behavior.

## Simple Example

Using an `{{#sq-option-set}}{{/sq-option-set}}`, you can easily access the
selected option by binding to its value.

```example
@title Basic Example

@controller-properties
{
  value: 'vanilla'
}

@hbs
{{#sq-option-set value=value}}
  {{#sq-option value="vanilla" optionClickedAction=(action (mut value))}} Vanilla Victory {{/sq-option}}
  {{#sq-option value="chocolate" optionClickedAction=(action (mut value))}} Chocolate Caravan {{/sq-option}}
  {{#sq-option value="strawberry" optionClickedAction=(action (mut value))}} Surreal Strawberry {{/sq-option}}
{{/sq-option-set}}
<hr />
Selected value is: {{value}}
```

Without an option set, each `{{sq-option}}` will be bound to its
`parentView` as it's created. The `parentView` object needs to implement
`addManagedOption` and `managedOptionClicked`.

### Multiple Select

Setting multiple to true on the option-set allows for multiple selections.

```handlebars
{{#sq-option-set multiple=true value=milkshake.flavor}}
  {{#sq-option value="vanilla"}} Vanilla Victory {{/sq-option}}
  {{#sq-option value="chocolate"}} Chocolate Caravan {{/sq-option}}
  {{#sq-option value="strawberry"}} Surreal Strawberry {{/sq-option}}
{{/sq-option-set}}
```

## Kitchen Sink Example

You can extend a sq-option to be rendered as a div (by default), an input
(radio or checkbox) or anything you'd like.

```handlebars
{{!-- options can be a crazy mix of divs, radio or checkbox inputs --}}
{{#sq-option-set value=milkshake.flavor}}
  {{#sq-option value="vanilla" tagName="input" type="radio"}} Vanilla Victory radio {{/sq-option}}
  {{#sq-option value="chocolate" tagName="input" type="checkbox"}} Chocolate Caravan checkbox {{/sq-option}}
  {{#sq-option value="strawberry"}} Surreal Strawberry div {{/sq-option}}
{{/sq-option-set}}

{{!-- An option set can be styled and used like a `<select>` tag --}}
{{#sq-option-set value=milkshake.flavor class="select"}}
  {{#sq-option value="vanilla" class="select-option"}} Vanilla Victory {{/sq-option}}
  {{#sq-option value="chocolate" class="select-option"}} Chocolate Caravan {{/sq-option}}
  {{#sq-option value="strawberry" class="select-option"}} Surreal Strawberry {{/sq-option}}
{{/sq-option-set}}
```
*/

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['option-set'],
  classNameBindings: ['isInvalid:input-is-invalid'],
  value: null,
  multiple: false,
  isInvalid: false,
  init(...args) {
    this._super(...args);
    if (!this.value && this.multiple) {
      this.set('value', A()); // setting ember array explicitly so addon can use methods: removeObject and pushObject
    }
  },
  actions: {
    multipleOptionClicked(selected, clickedValue) {
      const value = this.value;
      if (value.includes(clickedValue)) {
        value.removeObject(clickedValue);
      } else {
        value.pushObject(clickedValue);
      }
      this.onChange?.(value, clickedValue, selected);
    },
    singleOptionClicked(clickedValue, selected) {
      if (this.value !== clickedValue) {
        this.set('value', clickedValue);
        this.onChange?.(clickedValue, selected);
      }
    }
  }
});